import axios from 'axios';
import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import Select from 'react-select';
import { useForm, SubmitHandler, Controller, FieldError } from 'react-hook-form';
import { reduxConnect } from '../../common';
import type { IStoreState } from '../common/interfaces/types';
import { useIsWebView } from '../../common/hooks/useIsWebView';
import { mobileAppInstanceQueryParam, statesListStyles, usStatesOptions } from './constants';
import styled, { css } from 'styled-components';
import { largeMediaQuery, mediumMediaQuery, smallMediaQuery } from '../../common/utilities/sizeHelpers';
import { getQueryParamByName } from '../../common/utilities/queryParameters';
import { CheckboxOptions } from './constants';
import type { PrivacyFeedbackFormData, PrivacyRequestV1Verbose, PrivacyResponseV1Verbose } from './types';
import { PrivacyFeedbackCheckboxes } from './PrivacyFeedbackCheckboxes';
import { ErrorMessage } from './sharedStyledComponents';

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { user } = storeState?.appState?.appState || {};
    return {
        webUserAppInstanceId: user?.DeviceId
    };
};

const mapDispatchToProps = {};

export type PrivacyFeedbackProps = {
    webUserAppInstanceId: string;
}

export const PrivacyFeedback = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ webUserAppInstanceId }: PrivacyFeedbackProps) =>  {
    const [submittedFormIsPending, setSubmittedFormIsPending] = useState(false);
    const [submittedFormDisplayMessage, setSubmittedFormDisplayMessage] = useState('');
    const [submissionErrorDisplayMessage, setSubmissionErrorDisplayMessage] = useState('');
    const isWebView = useIsWebView();
    const mobileAppInstanceId = getQueryParamByName(mobileAppInstanceQueryParam);

    const {
        register,
        handleSubmit,
        setValue,
        setError,
        clearErrors,
        control,
        formState: { errors }
    } = useForm<PrivacyFeedbackFormData>();

    const onSubmit: SubmitHandler<PrivacyFeedbackFormData> = async (data) => {
        const { emailAddress, appInstanceId, stateOfResidence, requestDetails, checkboxes } = data;
        try {
            
            if (!checkboxes?.length) {
                setError('checkboxes', { message: 'Please select at least one of the options above in order to process your request.' });
                return;
            } else {
                clearErrors('checkboxes');
            }

            const feedbackReqBody: PrivacyRequestV1Verbose = {
                EmailAddress: DOMPurify.sanitize(emailAddress),
                AppInstanceId: DOMPurify.sanitize(appInstanceId),
                StateOfResidence: DOMPurify.sanitize(stateOfResidence.label),
                RequestDetails: DOMPurify.sanitize(requestDetails),
                IsDpe: checkboxes?.includes(CheckboxOptions[0]) ?? false,
                IsSdre: checkboxes?.includes(CheckboxOptions[1]) ?? false
            };

            setSubmittedFormIsPending(true);

            const baseResponse = (await axios.post<PrivacyResponseV1Verbose>('/api/privacyfeedback/rights', feedbackReqBody))?.data;

            if (baseResponse?.Submitted ) {
                setSubmittedFormDisplayMessage(baseResponse.DisplayMessage);
                setSubmissionErrorDisplayMessage('');
            } else {
                setSubmissionErrorDisplayMessage('There was an error while processing your request. Please try again later');
                setSubmittedFormDisplayMessage('');
            }
            setSubmittedFormIsPending(false);
        } catch (error) {
            console.error('error: ', error);
            // eslint-disable-next-line no-extra-parens
            setSubmissionErrorDisplayMessage('There was an error while processing your request. Please try again later');
            setSubmittedFormDisplayMessage('');
            setSubmittedFormIsPending(false);
        }
    };

    useEffect(() => {
        if (!isWebView && webUserAppInstanceId && !mobileAppInstanceId) {
            setValue('appInstanceId', webUserAppInstanceId, { shouldValidate: true });
        } else if (mobileAppInstanceId) {
            setValue('appInstanceId', mobileAppInstanceId, { shouldValidate: true });
        } else {
            setValue('appInstanceId', webUserAppInstanceId, { shouldValidate: true });
        }
    }, [isWebView, webUserAppInstanceId, mobileAppInstanceId, setValue]);

    return (
        <Container>
            <ContentSection>
                <Header>U.S. State Privacy Rights Requests</Header>
                <SectionDescription>Specific U.S. states grant individuals rights under state privacy laws with respect to personal information we collect. As per the WeatherBug Privacy Policy, to exercise these rights, please complete the form below. Note that we may need to request additional information from you to verify your identity or understand the scope of your request.</SectionDescription>
                {
                    !submittedFormDisplayMessage 
                        ? <>
                            <FormContainer onSubmit={handleSubmit(onSubmit)}>
                                <Label htmlFor='email'>Email</Label>
                                <Input
                                    id='email'
                                    type='email'
                                    placeholder='Your Email Address'
                                    {...register('emailAddress', { 
                                        required: true, 
                                        pattern: {
                                            value: /\S+@\S+\.\S+/,
                                            message: 'Entered value does not match email format'
                                        } 
                                    })} 
                                />
                                {errors.emailAddress && <><br/><ErrorMessage role='alert'>{'Email address is required'}</ErrorMessage><br/></>}

                                <Label htmlFor='appinstanceid'>AppInstanceId</Label>
                                <Input
                                    id='appinstanceid'
                                    type='text'
                                    readOnly
                                    placeholder='Your appinstanceid (if applicable)'
                                    {...register('appInstanceId', { 
                                        required: 'AppInstanceId cannot be empty',
                                        minLength: { value: 8, message: 'Minimum length is 8 characters.' },
                                        maxLength: { value: 36, message: 'Maximum length is 36 characters.' }
                                    })} 
                                />
                                {errors.appInstanceId && <ErrorMessage role='alert'>{errors.appInstanceId?.message ?? ''}</ErrorMessage>}
                                <br/>
                                <Label htmlFor='stateofresidence'>Your State of Residence</Label>
                                <Controller 
                                    name='stateOfResidence'
                                    control={control}
                                    rules={{
                                        required: 'State is required. Please select your state of residence.'
                                    }}
                                    render={({ field }) => {
                                        return (
                                            <Select 
                                                { ...field }
                                                styles={statesListStyles}
                                                options={usStatesOptions as any} 
                                                inputId='stateofresidence'
                                                placeholder='Select a state' />
                                        );
                                    }}
                                />
                                {errors.stateOfResidence && <><br/> <ErrorMessage role='alert'>{errors.stateOfResidence.message}</ErrorMessage> <br/></>}
                                <PrivacyFeedbackCheckboxes name='checkboxes' control={control} error={errors.checkboxes as FieldError} options={CheckboxOptions} setError={setError} clearErrors={clearErrors} />
                                <Label htmlFor='requestdetails'>Request Details (e.g., opt-out, deletion, correction, access):</Label>
                                <TextArea
                                    id='requestdetails'
                                    placeholder='Provide additional information (optional)'
                                    {...register('requestDetails', { 
                                        required: false,
                                        maxLength: { value: 500, message: 'Your Request details cannot be more than 500 characters long' } 
                                    })} 
                                />
                                {errors.requestDetails && <ErrorMessage role='alert'>{errors.requestDetails.message}</ErrorMessage>}
                                <ImportantNote>
                                    Please note, in the event you are not a registered WeatherBug user, and you access WeatherBug both
                                    <RomanList>
                                        <li>via our app through one or more mobile devices and/or</li>
                                        <li>via one or more browsers on a desktop computer,</li>
                                    </RomanList>
                                    you will need to submit a separate request using this webform through each mobile device and/or browser you use to access WeatherBug.
                                    <p>To do this through your mobile device(s), open the WeatherBug app on the applicable mobile device, navigate to the privacy policy from the menu icon in the top left corner, and then click on the https://www.weatherbug.com/privacyfeedback link in the privacy policy to access the webform in order to submit your request.</p>
                                    <p>To do this through your browser(s), open the WeatherBug website in the applicable browser, navigate to the privacy policy using the “Privacy” link at the bottom of the homepage, and then click on the https://www.weatherbug.com/privacyfeedback link in the privacy policy to access the webform in order to submit your request.</p>
                                </ImportantNote>
                                <Button type='submit'>{ submittedFormIsPending ? 'Submitting...' : 'Submit' }</Button>
                            </FormContainer>
                        </>
                        : null
                }
                {submittedFormDisplayMessage && <><br/><SuccessfullySubmittedFormMessage>{submittedFormDisplayMessage}</SuccessfullySubmittedFormMessage><br/></>}
                {submissionErrorDisplayMessage && <><br/><ErrorFormMessage>{submissionErrorDisplayMessage}</ErrorFormMessage><br/></>}
            </ContentSection>
        </Container>
    );
});

const Container = styled.div`
    display: flex;
    flex-direction: column;
    max-width: calc(1218px + 8%);
    padding-left: 4%;
    padding-right: 4%;
    margin: 0 auto;

    @media ${mediumMediaQuery} {
        width: 100%;
        margin-left: auto;
        margin-right: auto;
        padding-left: 15px;
        padding-right: 15px;
    }

    @media ${smallMediaQuery} {
        width: 100%;
        padding-left: 10px;
        padding-right: 10px;
        margin-left: auto;
        margin-right: auto;
    }
`; 

const ContentSection = styled.section`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: auto;
    padding: 20px;
    max-width: 600px;
    font-family: Source Sans Pro;
    font-size: 16px;
`;

const Header = styled.h1`
    font-size: 18px;
`;

const SectionDescription = styled.p`
    font-size: 16px;
`;

const FormContainer = styled.form`
    width: 100%;
    gap: 15px;

    @media ${mediumMediaQuery} {
        max-width: 500;
    }

    @media ${smallMediaQuery} {
        max-width: 95%;
    }
`;

const Label = styled.label`
    font-size: 14px;
    margin-bottom: 3px;
    font-weight: bold;
`;

const Input = styled.input`
    color: #000000;
    width: 100%;
    padding: 8px;
    margin-bottom: 9px;
    box-sizing: border-box;
`;

const TextArea = styled.textarea`
    color: #000000;
    width: 100%;
    padding: 8px;
    height: 100px;
    margin-bottom: 15px;
    box-sizing: border-box;
`;

const Button = styled.button`
    display: block;
    text-align: center;
    border: none;
    color: #ffffff;
    width: 39%;
    border-radius: 5px;
    padding: 10px 20px;
    cursor: pointer;
    line-height: 21px;
    background-color: #0056B3;
    transition: background-color 0.3s;

    @media ${largeMediaQuery} {
        background-color: #2f6f8c;
    }

    &:hover {
        background-color: #0056B3;
    }
`;

const CommonSubmittedFormStyles = css`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #ffffff;
    color: #0B6623;
    font-weight: bold;
    font-size: 16px;
    padding: 3px 13px;
    margin-top: 13px;
    height: 37px;
    width: 80%;
`;

const SuccessfullySubmittedFormMessage = styled.div`
    ${CommonSubmittedFormStyles}
`;

const ErrorFormMessage = styled.div`
    ${CommonSubmittedFormStyles}
    color: red;
`;

const ImportantNote = styled.div`
    background-color: rgba(0, 0, 0, 0.7);
    color: #ffffff;
    padding: 16px;
    width: 100%;
    margin-bottom: 9px;
`;

const RomanList = styled.ol`
    list-style-type: lower-roman;
    padding-left: 20px;
`;